/**
 * Football terms that arrive as English text in the data feed — card and
 * VAR reasons ("Tripping", "Goal Disallowed - offside") and why a player is
 * unavailable ("Knee Injury", "Ankle/Foot Injury", "Red Card") — mapped to
 * one translation key each: `term.reason.<slug>` / `term.absence.<slug>`.
 *
 * The feed spells the same thing several ways ("Hamstring", "Hamstring
 * Injury"; "MCL Knee Injury", "MCL Knee Ligament Injury"; "Heart Problems",
 * "Cardiac Condition"), so lookups go through normalize() and ALIASES.
 * Generated from footballmanianweb3 shared/utils/feedTerms.ts (keep in sync).
 * The list comes from the strings in ~15,000 recent match, team and player
 * files (2026-10-11); unknown strings are shown as they come.
 */

/** "Ankle/Foot Injury" -> "ankle_foot_injury" */
export function normalizeFeedTerm(raw) {
  return raw.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

export const REASON_SLUGS = [
  'foul', 'holding', 'tripping', 'roughing', 'diving', 'elbowing', 'delay_of_game', 'unsportsmanlike_conduct',
  'serious_foul', 'handling', 'not_on_pitch', 'head_butting', 'abuse_of_officials', 'misses_next_match',
  'goal_disallowed', 'goal_disallowed_offside', 'goal_disallowed_foul', 'goal_disallowed_video_review',
];

const REASON_ALIASES = {
  serious_foul_play: 'serious_foul',
  handball: 'handling',
  head_butt: 'head_butting',
  headbutt: 'head_butting',
  unsporting_behaviour: 'unsportsmanlike_conduct',
  time_wasting: 'delay_of_game',
  goal_disallowed_var: 'goal_disallowed_video_review',
};

export const ABSENCE_SLUGS = [
  'suspended', 'red_card', 'yellow_cards', 'doping', 'inactive', 'off_the_roster', 'coach_decision',
  'international_duty', 'loan_agreement', 'transfer_negotiations', 'personal_reasons', 'rest', 'tired',
  'lacking_match_fitness', 'unknown', 'injury', 'knock', 'minor_injury', 'illness', 'virus', 'health_problems',
  'medical_condition', 'stress', 'surgery', 'heart_condition', 'appendicitis', 'tonsillitis', 'pneumonia', 'cancer',
  'bowel_disease', 'dental_problems', 'hernia', 'inguinal_hernia', 'concussion', 'contusion', 'head_injury',
  'facial_injury', 'eye_injury', 'mouth_injury', 'jaw_injury', 'broken_jaw', 'broken_nose', 'fractured_cheekbone',
  'neck_injury', 'shoulder_injury', 'dislocated_shoulder', 'collarbone_injury', 'broken_collarbone',
  'upper_body_injury', 'chest_injury', 'rib_injury', 'back_injury', 'lower_back_injury', 'abdominal_strain',
  'arm_injury', 'broken_arm', 'elbow_injury', 'wrist_injury', 'broken_wrist', 'hand_injury', 'broken_hand',
  'hand_wrist_injury', 'thumb_wrist_injury', 'finger_injury', 'broken_finger', 'lower_body_injury',
  'pelvis_injury', 'groin_injury', 'groin_strain', 'groin_pelvis_injury', 'hip_injury', 'hip_thigh_injury',
  'gluteal_strain', 'thigh_injury', 'thigh_muscle_strain', 'thigh_muscle_rupture', 'hamstring_injury',
  'muscle_injury', 'leg_injury', 'broken_leg', 'knee_injury', 'acl_injury', 'mcl_injury', 'lcl_injury',
  'pcl_injury', 'cruciate_ligament_injury', 'meniscus_injury', 'shin_injury', 'broken_shinbone',
  'broken_calfbone', 'calf_injury', 'calf_muscle_strain', 'calf_shin_injury', 'achilles_tendon_injury',
  'achilles_strain', 'achilles_tendon_rupture', 'achilles_tendinitis', 'tendon_injury', 'ankle_injury',
  'ankle_ligaments', 'broken_ankle', 'ankle_foot_injury', 'foot_injury', 'broken_foot', 'metatarsal_fracture',
  'heel_injury', 'toe_injury', 'broken_toe', 'jumpers_knee', 'stomach_disorder', 'gashed_leg',
];

const ABSENCE_ALIASES = {
  injury_suspended: 'suspended',
  suspension: 'suspended',
  red_card_suspension: 'red_card',
  yellow_card: 'yellow_cards',
  yellow_card_suspension: 'yellow_cards',
  coach_s_decision: 'coach_decision',
  not_in_squad: 'off_the_roster',
  rested: 'rest',
  fatigue: 'tired',
  hamstring: 'hamstring_injury',
  heart_problems: 'heart_condition',
  cardiac_condition: 'heart_condition',
  acl_knee_injury: 'acl_injury',
  acl_knee_ligament_injury: 'acl_injury',
  mcl_knee_injury: 'mcl_injury',
  mcl_knee_ligament_injury: 'mcl_injury',
  lcl_knee_injury: 'lcl_injury',
  lcl_knee_ligament_injury: 'lcl_injury',
  pcl_knee_injury: 'pcl_injury',
  pcl_knee_ligament_injury: 'pcl_injury',
  knee_ligament_injury: 'knee_injury',
  ribs_injury: 'rib_injury',
  ankle_fracture: 'broken_ankle',
  ankle_ligaments_injury: 'ankle_ligaments',
  achilles_tendonitis: 'achilles_tendinitis',
  thigh_muscle_injury: 'thigh_muscle_strain',
  fractured_arm: 'broken_arm',
  fractured_hand: 'broken_hand',
  bruise: 'contusion',
  jumper_s_knee: 'jumpers_knee',
  patellar_tendinitis: 'jumpers_knee',
};

const REASONS = new Set(REASON_SLUGS);
const ABSENCES = new Set(ABSENCE_SLUGS);

/** Translation key for a card/VAR reason, or null when the feed text is unknown */
export function reasonKey(raw) {
  const n = normalizeFeedTerm(raw);
  const slug = REASON_ALIASES[n] ?? n;
  return REASONS.has(slug) ? `term.reason.${slug}` : null;
}

/** Translation key for an absence (injury, suspension …), or null when unknown */
export function absenceKey(raw) {
  const n = normalizeFeedTerm(raw);
  const slug = ABSENCE_ALIASES[n] ?? n;
  return ABSENCES.has(slug) ? `term.absence.${slug}` : null;
}
